import * as React from 'react';
import clsx from 'clsx';
import elipseImage from '@/assets/images/elements/elipse.webp';
import type { UtmEchoChatMessage } from '@/components/layout/auth/utmEchoConfig';

interface AuthChatPreviewCompactProps {
  avatar: string;
  name: string;
  messages: UtmEchoChatMessage[];
}

/** Compact chat preview for collapsed UTM mobile view: large avatar, name overlay, blur divider, 2 bubbles. */
export const AuthChatPreviewCompact: React.FC<AuthChatPreviewCompactProps> = ({
  avatar,
  name,
  messages,
}) => {
  const textMessages = messages.filter((msg) => msg.type === 'text' && msg.text);
  const echoBubble = textMessages.find((msg) => msg.sender === 'echo');
  const userMessages = textMessages.filter((msg) => msg.sender === 'user');
  const userBubble = userMessages[1] ?? userMessages[0];

  return (
    <div className='mt-2 flex w-full flex-1 flex-col items-start'>
      {/* Header: avatar box with glow behind */}
      <div className='relative flex h-[180px] w-full shrink-0 flex-col items-center justify-center overflow-hidden'>
        {/* Glow behind avatar */}
        <img
          src={elipseImage}
          alt=''
          className='pointer-events-none absolute left-1/2 top-0 !h-[300px] !w-[500px] -translate-x-1/2 object-contain'
        />
        {/* Avatar */}
        <div className='relative z-10 h-full w-[220px]'>
          <img
            src={avatar}
            alt={name}
            className='size-full object-cover object-top'
            style={{ maskImage: 'linear-gradient(to bottom, black 100%, transparent 100%)' }}
          />
        </div>

        {/* Blur divider at base — in front of image, behind name */}
        <div className='pointer-events-none absolute bottom-[-10px] left-0 z-20 flex h-[120px] w-full translate-y-1/2 flex-col items-center justify-center'>
          <div
            className='absolute inset-0 size-full'
            style={{
              background:
                'linear-gradient(180deg, transparent 0%, #2C204C 25%, #2C204C 75%, transparent 100%)',
            }}
          />
        </div>

        {/* Name at base — above blur divider */}
        <h3 className='relative z-30 -mt-6 font-proxima text-[15px] font-bold leading-tight text-white'>
          {name}
        </h3>
      </div>

      {/* Chat bubbles */}
      <div className='relative flex w-full flex-col gap-2 overflow-hidden px-[15px]'>
        {/* Blur divider flipped — behind bubbles, fades down from top */}
        <div className='pointer-events-none absolute left-0 top-0 z-0 h-[60px] w-full'>
          <div
            className='absolute inset-0 size-full'
            style={{
              background: 'linear-gradient(180deg, #2C204C 0%, transparent 100%)',
            }}
          />
        </div>
        {userBubble?.text && (
          <div className='flex w-full justify-end'>
            <div
              className={clsx(
                'w-fit max-w-[95%] rounded-24 px-4 py-3',
                'glass-bubble backdrop-blur-none',
                'bg-user-bubble-gradient text-white',
                'font-sans text-12 font-medium leading-160 tracking-neg-2'
              )}
            >
              <p className='whitespace-pre-wrap break-words'>{userBubble.text}</p>
            </div>
          </div>
        )}

        {echoBubble?.text && (
          <div className='flex w-full justify-start'>
            <div
              className={clsx(
                'w-fit max-w-[95%] rounded-24 px-4 py-3',
                'glass-bubble backdrop-blur-none',
                'bg-gradient-50 text-white',
                'font-sans text-12 font-medium leading-160 tracking-neg-2'
              )}
            >
              <p className='whitespace-pre-wrap break-words'>{echoBubble.text}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
